import { SocItem } from "../../../domain/SocItem";
import { SectionProps } from "../SectionProps";
import { Controller } from "react-hook-form";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { fieldLabels } from "../fieldLabels";
import { Error } from "../../common/Error";
import { Grid, InputAdornment, Typography } from "@material-ui/core";

export const QuestionTwoSection: React.FC<SectionProps<SocItem>> = (
  sectionProps
) => {
  const { control, disabled, errors } = sectionProps;

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="body1">
          Please provide your forecast for the following metrics for your
          2022-2023 accounting year.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="forecastRevenueIncome"
          rules={{ required: true, min: 0 }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.forecastRevenueIncome}
              readOnly={disabled}
              data-test="forecastRevenueIncome-field"
              InputProps={{
                inputProps: {
                  min: 0,
                  pattern: {
                    value: /^((([1-9][0-9]{0,2}(?:,[0-9]{3}){0,5})?)|0|([1-9][0-9]{0,17}))(?:\.[0-9]{1,10})?$/,
                    message:
                      "Entered value does not match the format of currency",
                  },
                },
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
          }
        />
        {errors.forecastRevenueIncome?.type === "required" && (
          <Error data-test="forecastRevenueIncome-validation">
            This field is required
          </Error>
        )}
        {errors.forecastRevenueIncome?.type === "min" && (
          <Error data-test="forecastRevenueIncome-validation">
            Must be greater than 0
          </Error>
        )}
        {errors.forecastRevenueIncome?.type === "pattern" && (
          <Error data-test="forecastRevenueIncome-validation">
            {errors.forecastRevenueIncome.message}
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="forecastExpenditure"
          rules={{ required: true, min: 0 }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.forecastExpenditure}
              readOnly={disabled}
              data-test="forecastExpenditure-field"
              InputProps={{
                inputProps: { min: 0, pattern: "[0-9]*" },
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
          }
        />
        {errors.forecastExpenditure?.type === "required" && (
          <Error data-test="forecastExpenditure-validation">
            This field is required
          </Error>
        )}
        {errors.forecastExpenditure?.type === "min" && (
          <Error data-test="forecastExpenditure-validation">
            Must be greater than 0
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="forecastProfitSurplus"
          rules={{ required: true }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.forecastProfitSurplus}
              readOnly={disabled}
              data-test="forecastProfitSurplus-field"
              InputProps={{
                inputProps: { min: 0, pattern: "[0-9]*" },
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
          }
        />
        {errors.forecastProfitSurplus?.type === "required" && (
          <Error data-test="forecastProfitSurplus-validation">
            This field is required
          </Error>
        )}
        {errors.forecastProfitSurplus?.type === "pattern" && (
          <Error data-test="forecastProfitSurplus-validation">
            {errors.forecastProfitSurplus.message}
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="forecastCashLiquidReserves"
          rules={{ required: true, min: 0 }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.forecastCashLiquidReserves}
              readOnly={disabled}
              data-test="forecastCashLiquidReserves-field"
              InputProps={{
                inputProps: { min: 0, pattern: "[0-9]*" },
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
          }
        />
        {errors.forecastCashLiquidReserves?.type === "required" && (
          <Error data-test="forecastCashLiquidReserves-validation">
            This field is required
          </Error>
        )}
        {errors.forecastCashLiquidReserves?.type === "min" && (
          <Error data-test="forecastCashLiquidReserves-validation">
            Must be greater than 0
          </Error>
        )}
        {errors.forecastCashLiquidReserves?.type === "pattern" && (
          <Error data-test="forecastCashLiquidReserves-validation">
            {errors.forecastCashLiquidReserves.message}
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="forecastNumberOfEmployees"
          rules={{ required: true, min: 0 }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.forecastNumberOfEmployees}
              readOnly={disabled}
              data-test="forecastNumberOfEmployees-field"
              InputProps={{ inputProps: { min: 0, pattern: "[0-9]*" } }}
            />
          }
        />
        {errors.forecastNumberOfEmployees?.type === "required" && (
          <Error data-test="forecastNumberOfEmployees-validation">
            This field is required
          </Error>
        )}
        {errors.forecastNumberOfEmployees?.type === "min" && (
          <Error data-test="forecastNumberOfEmployees-validation">
            Must be greater than 0
          </Error>
        )}
        {errors.forecastNumberOfEmployees?.type === "pattern" && (
          <Error data-test="forecastNumberOfEmployees-validation">
            {errors.forecastNumberOfEmployees.message}
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="forecastNumberOfContractors"
          rules={{ required: true, min: 0 }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.forecastNumberOfContractors}
              readOnly={disabled}
              data-test="forecastNumberOfContractors-field"
              InputProps={{ inputProps: { min: 0, pattern: "[0-9]*" } }}
            />
          }
        />
        {errors.forecastNumberOfContractors?.type === "required" && (
          <Error data-test="forecastNumberOfContractors-validation">
            This field is required
          </Error>
        )}
        {errors.forecastNumberOfContractors?.type === "min" && (
          <Error data-test="forecastNumberOfContractors-validation">
            Must be greater than 0
          </Error>
        )}
        {errors.forecastNumberOfContractors?.type === "pattern" && (
          <Error data-test="forecastNumberOfContractors-validation">
            {errors.forecastNumberOfContractors.message}
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{ maxLength: 5000 }}
          name="forecastFurtherInformation"
          as={
            <TextField
              label={fieldLabels.furtherInformation}
              readOnly={disabled}
              data-test="forecastFurtherInformation-field"
              rows={8}
              multiline
            />
          }
        />
        {errors.forecastFurtherInformation?.type === "maxLength" && (
          <Error data-test="forecastFurtherInformation-validation">
            Must be no more than 5,000 characters
          </Error>
        )}
      </Grid>
    </>
  );
};
